import { Component, OnInit, ViewChild } from '@angular/core';
import { routes } from '@app/consts';
import { TranslateService } from '@ngx-translate/core';
import { GridComponent, GridDataResult } from '@progress/kendo-angular-grid';
import { OverlayService } from '@app/shared/components/overlay/overlay.service';
import { GridState } from '@app/shared/services/grid-state/grid-state.model';
import moment from 'moment';
import { from } from 'rxjs';
import { BuySellService } from '@app/modules/time-off/services/buy-sell.service';

@Component({
  selector: 'app-timeoff-buy-sell-status-report',
  templateUrl: './timeoff-buy-sell-status-report.component.html',
  styleUrls: ['./timeoff-buy-sell-status-report.component.scss']
})
export class TimeoffBuySellStatusReportComponent implements OnInit {
  @ViewChild('excelexport') excelexport: any;
  public grid: GridComponent;
  public routes: typeof routes = routes;
  public bindingType = 'array';
  public gridDataResult: GridDataResult;
  excelData: any;
  gridId: string = 'TimeOffBuySellStatusDataGrid';
  isLoading: boolean;
  pageSize: number = 20;
  skip: number = 0;

  sortString: string;
  filterString: string;

  excelColumns: any[] = [
    { field: 'employee', title: this.translate.instant('BuySell-Employee') },
    { field: 'plan', title: this.translate.instant('BuySell-Plan') },
    { field: 'reviewers', title: this.translate.instant('BuySell-Reviewers') },
    { field: 'status', title: this.translate.instant('BuySell-Status') },
  ];

  public columns: any[] = [
    { field: 'employee', title: this.translate.instant('BuySell-Employee'), localizationCode: 'BuySell-Employee', type: 'employee' },
    { field: 'plan', subField: 'name', title: this.translate.instant('BuySell-Plan'), localizationCode: 'BuySell-Plan' },
    { field: 'reviewers', title: this.translate.instant('BuySell-Reviewers'), localizationCode: 'BuySell-Reviewers', type: 'employees' },
    { field: "status", subField: "name", title: this.translate.instant('BuySell-Status'), localizationCode: 'BuySell-Status', type: 'chip', chipValue: ['Approved', 'Denied', 'Pending'] },
  ];

  sortableColumns: any[] = [
    { field: 'employee', sortValue: ['employee.firstName', 'employee.lastName'] },
    { field: 'plan', sortValue: 'plan.name' },
    { field: 'status', sortValue: 'status.name' },
  ];

  filterCategories: any[] = [
    { field: "employee.firstname", title: this.translate.instant('EmployeeFirstname'), type: "String", dataType: 'String' },
    { field: "employee.lastname", title: this.translate.instant('EmployeeLastname'), type: "String", dataType: 'String' },
    { field: "plan.name", title: this.translate.instant('BuySell-Plan'), type: "String", dataType: 'String' },
    { field: "reviewer.firstName", title: this.translate.instant('BuySell-Reviewer'), type: "String", dataType: 'String' },
    { field: "reviewer.lastName", title: this.translate.instant('BuySell-Reviewer'), type: "String", dataType: 'String' },
    { field: "status.name", title: this.translate.instant('BuySell-Status'), type: "String", dataType: 'String' },
  ];

  searchValue: any;
  searchFilterString: string;
  getRequest: any;
  error: boolean;
  showAsOfDateField: boolean = true;
  asOfDate: any = moment().format('YYYY-MM-DD');
  fileName: string = this.translate.instant('TimeOffBuySellStatusReport') + '_' + moment().format('YYYY-MM-DD') + '.xlsx';
  baseFilter = `(published = "true")`;

  constructor(
    private translate: TranslateService,
    private overlayService: OverlayService,
    private buySellService: BuySellService
  ) {
    this.getData();

    // Bind 'this' explicitly to capture the execution context of the component.
    this.allData = this.allData.bind(this);
  }

  ngOnInit(): void {
  }

  public allData() {
    this.overlayService.show('Loading');

    let filter = this.baseFilter;
    if (this.filterString) {
      filter += ` AND (${this.filterString})`;
    }

    from(this.buySellService.getAllBuySellHoursReportData(filter, this.sortString, this.asOfDate, 'Reviewers'))
      .subscribe({
        next: (res) => {
          this.excelData = res.data;

          this.excelData.map((item) => {
            item.employee = `${item.employee.firstName} ${item.employee.lastName}`;
            item.reviewers = item.reviewers.map(r => `${r.reviewer.firstName} ${r.reviewer.lastName}`).join(', ');
            item.plan = item.plan?.name;
            item.type = item.type?.name;
            item.status = item.status?.name;
          });

          // if I dont have this timeout first time I download the report the data will be blank
          setTimeout(() => {
            this.excelexport.save();
          }, 1000);
        },
        error: (e) => {
          this.overlayService.hide();
        },
        complete: () => this.overlayService.hide()
      });
  }

  getData(): void {
    this.isLoading = true;

    let filter = this.baseFilter;
    if (this.filterString) {
      filter += ` AND (${this.filterString})`;
    }

    this.getRequest = this.buySellService.getBuySellPlanEmployees(this.pageSize, this.skip, filter, this.sortString, this.asOfDate, 'Reviewers')
      .subscribe(
        {
          next: (v) => {
            v.data.map((item) => {
              item.reviewers = item.reviewers.map(r => ({id: r.reviewer.id, firstName: r.reviewer.firstName, lastName: r.reviewer.lastName}));
            });
            this.gridDataResult = {
              data: v.data,
              total: v.total,
            }
          },
          error: (e) => {
            this.error = true;
            this.isLoading = false;
          },
          complete: () => this.isLoading = false
        }
      );
  }

  gridStateChange(gridState: GridState) {
    if (this.pageSize !== gridState.pageSize) {
      this.pageSize = gridState.pageSize;
    }
    if (this.skip !== gridState.skip) {
      this.skip = gridState.skip;
    }
    if (this.sortString !== gridState.sortString) {
      this.sortString = gridState.sortString;
    }
    if (this.filterString !== gridState.filter) {
      this.filterString = gridState.filter;
      this.skip = 0;
    }
    if (this.asOfDate !== gridState.asOfDate) {
      this.asOfDate = gridState.asOfDate;
    }

    this.getRequest.unsubscribe();
    this.getData();
  }
}
